import { fromJS } from 'immutable'
import {
  SEND_CHAT_MESSAGE,
  SEND_CHAT_MESSAGE_SUCCESS,
  SEND_CHAT_MESSAGE_ERROR,
  SEND_CHAT_MESSAGE_STATUS,
  SEND_CHAT_MESSAGE_STREAM_CHUNK,
  CLEAR_CHAT_HISTORY,
  TOGGLE_CHAT_WINDOW,
  SET_CHAT_WINDOW_OPEN,
  SET_CHAT_LOADING,
  SET_CONVERSATION_ID,
  LOAD_CONVERSATIONS,
  LOAD_CONVERSATIONS_SUCCESS,
  LOAD_CONVERSATIONS_ERROR,
  LOAD_CONVERSATION,
  LOAD_CONVERSATION_SUCCESS,
  LOAD_CONVERSATION_ERROR,
  DELETE_CONVERSATION,
  DELETE_CONVERSATION_SUCCESS,
  DELETE_CONVERSATION_ERROR,
  UPDATE_CONVERSATION_TITLE,
  RESET_CHAT_SCOPE,
  SET_CHAT_FULLSCREEN,
} from './constants'

const initialState = fromJS({
  messages: [],
  conversationId: null,
  scopeCode: null,
  conversations: [],
  conversationsLoading: false,
  deletingConversationId: null,
  deleteError: null,
  isLoading: false,
  error: null,
  isOpen: false,
  isFullscreen: false,
})

function chatReducer(state = initialState, action) {
  switch (action.type) {
    case SEND_CHAT_MESSAGE:
      return state
        .set('isLoading', true)
        .set('error', null)
        .set('statusMessage', null)
        .update('messages', (messages) =>
          messages.push(
            fromJS({
              role: 'user',
              content: action.message,
              timestamp: new Date().toISOString(),
            })
          )
        )
        .update('messages', (messages) =>
          messages.push(
            fromJS({
              role: 'assistant',
              content: '',
              sources: [],
              suggestedQuestions: [],
              timestamp: new Date().toISOString(),
              isStreaming: true,
            })
          )
        )

    case SEND_CHAT_MESSAGE_STREAM_CHUNK:
      return state.set('statusMessage', null).update('messages', (messages) => {
        const lastIndex = messages.size - 1
        if (lastIndex < 0) return messages

        const lastMessage = messages.get(lastIndex)
        if (!lastMessage || lastMessage.get('role') !== 'assistant') {
          return messages
        }

        return messages.update(lastIndex, (msg) =>
          msg.update('content', (content) => content + action.chunk)
        )
      })

    case SEND_CHAT_MESSAGE_SUCCESS: {
      const { conversationId, conversationTitle } = action.response

      let newState = state
        .set('isLoading', false)
        .set('statusMessage', null)
        .update('messages', (messages) => {
          const lastIndex = messages.size - 1
          if (lastIndex < 0) return messages

          const lastMessage = messages.get(lastIndex)
          if (lastMessage && lastMessage.get('isStreaming')) {
            return messages.update(lastIndex, (msg) =>
              msg
                .set('content', action.response.response)
                .set('sources', fromJS(action.response.sources || []))
                .set(
                  'suggestedQuestions',
                  fromJS(action.response.suggestedQuestions || [])
                )
                .set('tokensUsed', action.response.tokensUsed)
                .set('truncated', action.response.truncated === true)
                .set('timestamp', action.response.timestamp)
                .delete('isStreaming')
            )
          }

          return messages.push(
            fromJS({
              role: 'assistant',
              content: action.response.response,
              sources: action.response.sources || [],
              suggestedQuestions: action.response.suggestedQuestions || [],
              tokensUsed: action.response.tokensUsed,
              truncated: action.response.truncated === true,
              timestamp: action.response.timestamp,
            })
          )
        })

      if (conversationId) {
        newState = newState.set('conversationId', conversationId)
      }

      if (conversationId) {
        const now = new Date().toISOString()
        const idx = newState
          .get('conversations')
          .findIndex((c) => c.get('id') === conversationId)
        if (idx >= 0) {
          newState = newState.updateIn(['conversations', idx], (c) => {
            let updated = c
              .update('messageCount', (count) => (count ?? 0) + 2)
              .set('updatedAt', now)
            if (conversationTitle) {
              updated = updated.set('title', conversationTitle)
            }
            return updated
          })
        } else {
          newState = newState.update('conversations', (convs) =>
            convs.unshift(
              fromJS({
                id: conversationId,
                title: conversationTitle || null,
                messageCount: 2,
                createdAt: now,
                updatedAt: now,
              })
            )
          )
        }
      }

      return newState
    }

    case SEND_CHAT_MESSAGE_ERROR: {
      // Keep the question, drop the assistant bubble. This mirrors what the server actually holds:
      // the question is stored before the stream starts, deliberately, so that a failure leaves the
      // conversation with the question rather than empty — but the answer is stored only once the
      // stream completes, so a failed one was never persisted at all.
      //
      // Removing the question here as well was the mismatch: it vanished from the screen while
      // standing in the database, so retyping it sent the model the same question twice, once out of
      // the history and once as the new message.
      let newState = state
        .set('isLoading', false)
        .set('statusMessage', null)
        .set('error', action.error)
        .update('messages', (messages) => {
          const lastIndex = messages.size - 1
          if (lastIndex < 0) return messages

          const last = messages.get(lastIndex)
          return last &&
            last.get('role') === 'assistant' &&
            last.get('isStreaming')
            ? messages.pop()
            : messages
        })

      // A first message that failed after the conversation was created leaves the client without an
      // id, so the next attempt would start a second conversation and abandon the first.
      const failedConversationId = action.error?.conversationId
      if (failedConversationId && !newState.get('conversationId')) {
        newState = newState.set('conversationId', failedConversationId)
      }

      return newState
    }

    case CLEAR_CHAT_HISTORY:
      return state
        .set('messages', fromJS([]))
        .set('conversationId', null)
        .set('deletingConversationId', null)
        .set('error', null)
        .set('statusMessage', null)
        .set('isLoading', false)

    case TOGGLE_CHAT_WINDOW:
      return state.update('isOpen', (isOpen) => !isOpen)

    case SET_CHAT_WINDOW_OPEN:
      return state.set('isOpen', action.isOpen)

    case SET_CHAT_LOADING:
      return state.set('isLoading', action.isLoading)

    case SET_CONVERSATION_ID:
      return state.set('conversationId', action.id)

    case SEND_CHAT_MESSAGE_STATUS:
      return state.set('statusMessage', action.status)

    case LOAD_CONVERSATIONS:
      return state.set('conversationsLoading', true)

    case LOAD_CONVERSATIONS_SUCCESS:
      return state
        .set(
          'conversations',
          fromJS(
            Array.isArray(action.conversations) ? action.conversations : []
          )
        )
        .set('conversationsLoading', false)

    case LOAD_CONVERSATIONS_ERROR:
      return state.set('conversationsLoading', false)

    case LOAD_CONVERSATION:
      return state
        .set('isLoading', true)
        .set('error', null)
        .set('statusMessage', null)

    case LOAD_CONVERSATION_SUCCESS: {
      const msgs = (action.conversation.messages || []).map((m) => ({
        role: m.role,
        content: m.content,
        timestamp: m.timestamp,
        sources: [],
        suggestedQuestions: [],
      }))
      return state
        .set('messages', fromJS(msgs))
        .set('conversationId', action.conversation.id)
        .set('isLoading', false)
        .set('error', null)
    }

    case LOAD_CONVERSATION_ERROR:
      return state.set('isLoading', false).set('error', action.error)

    case DELETE_CONVERSATION:
      return state
        .set('deletingConversationId', action.conversationId)
        .set('deleteError', null)

    case DELETE_CONVERSATION_ERROR:
      return state
        .set('deletingConversationId', null)
        .set('deleteError', action.error)

    case DELETE_CONVERSATION_SUCCESS: {
      let newState = state.update('conversations', (convs) =>
        convs.filter((c) => c.get('id') !== action.conversationId)
      )
      newState = newState
        .set('deletingConversationId', null)
        .set('deleteError', null)
      if (state.get('conversationId') === action.conversationId) {
        newState = newState
          .set('messages', fromJS([]))
          .set('conversationId', null)
      }
      return newState
    }

    case UPDATE_CONVERSATION_TITLE: {
      const idx = state
        .get('conversations')
        .findIndex((c) => c.get('id') === action.conversationId)
      if (idx < 0) return state
      return state.updateIn(['conversations', idx], (c) =>
        c.set('title', action.title).set('updatedAt', new Date().toISOString())
      )
    }

    case RESET_CHAT_SCOPE:
      // The loading flags belong to the scope we are leaving. Carrying them over leaves the new
      // scope's input disabled or its spinner running forever whenever the destination does not
      // happen to start a request of its own.
      return state
        .set('messages', fromJS([]))
        .set('conversationId', null)
        .set('scopeCode', action.scopeCode ?? null)
        .set('conversations', fromJS([]))
        .set('conversationsLoading', false)
        .set('isLoading', false)
        .set('deletingConversationId', null)
        .set('deleteError', null)
        .set('error', null)
        .set('statusMessage', null)

    case SET_CHAT_FULLSCREEN:
      return state.set('isFullscreen', action.isFullscreen)

    default:
      return state
  }
}

export default chatReducer
